import { type ElementType, forwardRef, type ReactNode } from "react"
import { twMerge } from "tailwind-merge"
import { Icon } from "../icon"
import type { IconProps } from "../icon/Icon"
import { type Placement, Tooltip, type TooltipProps } from "../overlays/tooltip/Tooltip"
import { Button, type ButtonProps, type Size } from "./Button"

export type IconButtonProps = Omit<ButtonProps, "children" | "label"> & {
  /** An element that comes from the `@phosphor-icons/react` package. */
  icon: ElementType
  /** Props to pass to the icon such as `weight` */
  iconProps?: Omit<IconProps, "as">
  /** Placement of the tooltip label */
  labelPlacement?: Placement
  /** Props to pass to the tooltip popover */
  tooltipProps?: Omit<TooltipProps["tooltipProps"], "content" | "children">
  /** Props to pass to the tooltip trigger */
  tooltipTriggerProps?: TooltipProps["triggerProps"]
  // Must pass either label or aria-label
} & (
    | {
        /** Used as the aria-label and tooltip label if passed */
        label: string
      }
    | {
        /** Used as the aria-label and tooltip label */
        "aria-label": string
        label?: ReactNode
      }
  )

const sizeStyles: Record<Size, string> = {
  xsmall: "w-tt-xsmall h-tt-xsmall",
  small: "w-tt-small h-tt-small",
  medium: "w-tt-regular h-tt-regular",
  large: "w-tt-large h-tt-large",
}

export const makeIconButtonClassName = (size: Size) => {
  return twMerge("tt-icon-button p-0", sizeStyles[size])
}

/**
 * Trigger actions or events when clicked. Extends the [Button](./Button.tsx)
 * component but contains only an icon so it maintains a square shape. Is
 * wrapped in a `Tooltip` so the user can discover what the button does when
 * hovering.
 *
 * ```tsx
 * import { Star } from "@phosphor-icons/react"
 * import { IconButton } from "@tokenterminal/core/ui/button/IconButton"
 *
 * <IconButton icon={Star} label="Star this" onPress={...} />
 * ```
 */

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  {
    "aria-label": ariaLabel,
    className,
    icon,
    iconProps,
    tooltipProps,
    tooltipTriggerProps,
    size = "medium",
    ...props
  },
  ref,
) {
  let tooltipContent: ReactNode = ""
  let labelString: string = ""
  if ("label" in props && typeof props.label === "string") {
    tooltipContent = props.label
    labelString = props.label
  } else if ("label" in props) {
    tooltipContent = props.label
  }
  const tooltipPlacement: Placement = props.labelPlacement || "top"

  const button = (
    <Button
      ref={ref}
      className={twMerge(makeIconButtonClassName(size), className)}
      aria-label={ariaLabel || labelString}
      size={size}
      {...props}
    >
      <Icon as={icon} className="pointer-events-none" aria-hidden {...iconProps} />
    </Button>
  )

  if (!tooltipContent) {
    return button
  }

  return (
    <Tooltip
      content={tooltipContent}
      tooltipProps={{
        placement: tooltipPlacement,
        ...tooltipProps,
      }}
      triggerProps={tooltipTriggerProps}
    >
      {button}
    </Tooltip>
  )
})
